import React from 'react'
import { Box, Typography, Stack, Paper, Button } from '@mui/material'
import { Link as RouterLink } from 'react-router-dom'
import { useShoppingCart } from '../context/ShoppingCartContext'
import { ThemeProvider } from '@mui/system'
import theme, { Colors } from '../styles/theme/'

import { CartItem } from '../components/CartItem'

import {
    intPenniesToStr,
    getLineItemTotal,
    strToIntPennies,
} from '../utilities/money'

import SubmittalForm from '../components/submittalForm'
import Footer from '../components/footer'
import PageFooter from '../components/PageFooter.js'
import ToggleCurrency from '../components/toggleCurrency/ToggleCurrency'

export function ShoppingCart() {
    const { data, cart, emptyCart, isCAD } = useShoppingCart()
    // const { data, cart, emptyCart, dev_mode, isCAD } = useShoppingCart()

    const totalIntPennies = cart.reduce((total, cartItem) => {
        const item = data.find((i) => i.productIDNum === cartItem.productIDNum)
        const unitTotalStr = getLineItemTotal(
            cartItem.quantity,
            item?.srp || '0',
        )
        return total + strToIntPennies(unitTotalStr)
    }, 0)
    const curStr = isCAD ? ' CAD' : ''

    return (
        <>
            <ThemeProvider theme={theme}>
                <Box sx={{ mt: 2, px: { xs: 1, sm: 2, md: 0 } }}>
                    {/* ── Logo ── */}
                    <Stack
                        direction="row"
                        justifyContent={{ xs: 'center', sm: 'flex-start' }}
                    >
                        <Paper elevation={9} sx={{ mt: 2 }}>
                            <img
                                src="/shop/images/appbar/logo.png"
                                alt="ElectroBraid logo"
                                style={{
                                    display: 'block',
                                    maxWidth: '100%',
                                    height: 'auto',
                                }}
                            />
                        </Paper>
                    </Stack>

                    {/* ── Header: title + controls ── */}
                    {/* On mobile (xs/sm): stack vertically. On md+: side-by-side row */}
                    <Stack
                        direction={{ xs: 'column', md: 'row' }}
                        justifyContent="space-between"
                        alignItems={{ xs: 'flex-start', md: 'center' }}
                        sx={{ mt: 3, gap: { xs: 1.5, md: 0 } }}
                    >
                        <Typography
                            variant="h2"
                            sx={{
                                fontSize: {
                                    xs: '1.4rem',
                                    sm: '1.5rem',
                                    md: '2rem',
                                },
                                fontWeight: 600,
                                color: Colors.secondary,
                                textShadow: `${Colors.boltGold2} 0.5px 0.5px 0.5px, ${Colors.boltGold2} 0.25px 0.25px 0.25px`,
                            }}
                        >
                            Cart Review
                        </Typography>

                        {/* Controls: currency toggle can wrap above, buttons always together */}
                        <Stack
                            direction="row"
                            flexWrap="wrap"
                            alignItems="center"
                            justifyContent={{
                                xs: 'flex-start',
                                md: 'flex-end',
                            }}
                            sx={{
                                gap: { xs: 1.5, md: 3 },
                                width: { xs: '100%', md: 'auto' },
                            }}
                        >
                            <Box
                                sx={{
                                    width: { xs: '100%', md: 'auto' },
                                    ml: { xs: '0.6rem', md: 0 },
                                }}
                            >
                                <ToggleCurrency />
                            </Box>

                            <Stack
                                direction="row"
                                sx={{
                                    width: { xs: '100%', md: 'auto' },
                                    justifyContent: {
                                        xs: 'space-evenly',
                                        md: 'flex-end',
                                    },
                                    gap: { md: 2 },
                                }}
                            >
                                <Button
                                    variant="text"
                                    size="small"
                                    onClick={() => emptyCart()}
                                    sx={{
                                        px: { xs: 1.25, md: 2 },
                                        py: { xs: 0.5, md: 1 },
                                        fontSize: {
                                            xs: '0.75rem',
                                            md: '0.875rem',
                                        },
                                        whiteSpace: 'nowrap',
                                        color: Colors.secondary,
                                        borderColor: Colors.boltGold,
                                        background: 'none',
                                        '&:hover': {
                                            background: 'rgba(0, 82, 71, 0.08)',
                                            borderColor: Colors.greenLight,
                                            color: Colors.greenLight,
                                        },
                                    }}
                                >
                                    Empty Items
                                </Button>

                                <Button
                                    component={RouterLink}
                                    to="/"
                                    variant="outlined"
                                    size="small"
                                    sx={{
                                        px: { xs: 1.25, md: 2 },
                                        py: { xs: 0.5, md: 1 },
                                        fontSize: {
                                            xs: '0.75rem',
                                            md: '0.875rem',
                                        },
                                        whiteSpace: 'nowrap',
                                        color: Colors.secondary,
                                        borderColor: Colors.boltGold,
                                        background: 'none',
                                        '&:hover': {
                                            background: 'rgba(0, 82, 71, 0.08)',
                                            borderColor: Colors.greenLight,
                                            color: Colors.greenLight,
                                        },
                                    }}
                                >
                                    Return to Shopping
                                </Button>
                            </Stack>
                        </Stack>
                    </Stack>

                    {/* ── Cart items ── */}
                    {cart.length === 0 ? (
                        <Box sx={{ mt: 4, mb: 4, textAlign: 'center' }}>
                            <Typography
                                variant="h5"
                                sx={{ color: Colors.secondary }}
                            >
                                Your cart is empty
                            </Typography>
                            <Button
                                component={RouterLink}
                                to="/"
                                variant="contained"
                                sx={{ mt: 2 }}
                            >
                                Browse Products
                            </Button>
                        </Box>
                    ) : (
                        <>
                            <Box sx={{ mt: 2 }}>
                                {cart.map((item) => (
                                    <CartItem
                                        key={item.productIDNum}
                                        productIDNum={item.productIDNum}
                                        quantity={item.quantity}
                                        storeItems={data}
                                    />
                                ))}
                            </Box>

                            {/* ── Total ── */}
                            <Stack
                                sx={{
                                    mt: 2,
                                    px: { xs: 1, sm: 2, md: 0 },
                                    pr: { xs: 2.5, sm: 3, md: 2 },
                                }}
                                direction="row"
                                justifyContent="flex-end"
                                alignItems="center"
                            >
                                <Typography variant="h6">Total</Typography>
                                <Typography variant="h6" sx={{ ml: 2 }}>
                                    {intPenniesToStr(totalIntPennies)}
                                    {curStr}
                                </Typography>
                            </Stack>

                            {/* ── Checkout ── */}
                            <Stack
                                direction="row"
                                justifyContent="flex-end"
                                sx={{
                                    mt: 2,
                                    px: { xs: 1, sm: 2, md: 0 },
                                    pr: { xs: 2.5, sm: 3, md: 2 },
                                }}
                            >
                                <SubmittalForm
                                    data={data}
                                    cart={cart}
                                    isCAD={isCAD}
                                />
                            </Stack>
                        </>
                    )}
                </Box>

                <Footer />
                <PageFooter />
            </ThemeProvider>
        </>
    )
}
